import {
  AdminPartnerProviderConfiguration,
  UpsertAdminPartnerProviderRequest,
} from "@/types/admin-partner-provider";
import {
  AdminHotelAutoSyncResponse,
  AdminHotelPartnerSyncStatus,
  AdminHotelPartnerSyncTriggerResponse,
  AdminPartnerOrdersPollFeedResponse,
  AdminTenantPartnersSettings,
  PartnerHotelActivityLimit,
  TenantPartnerCommission,
  UpdateAdminHotelAutoSyncRequest,
  UpdatePartnerHotelActivityLimitRequest,
  UpdateTenantPartnerCommissionRequest,
} from "@/types/partner-settings";
import {
  PartnerProviderLogsFilters,
  PartnerProviderLogsResponse,
} from "@/types/partner-provider-log";
import {
  AdminPartnerOrdersSummary,
  PartnerOrdersImportHistoryEntry,
  PartnerOrdersImportResponse,
  PartnerOrdersQuery,
  PartnerOrdersResponse,
} from "@/types/partner-order";
import type { PartnerDateRangeQuery } from "@/types/partner-dashboard";
import type {
  AdminPartnerCategoryAutoAssignResponse,
  AdminPartnerCategoryGroup,
  AdminPartnerCategoryTag,
  CreateAdminPartnerCategoryGroupRequest,
  SetAdminPartnerCategoryGroupTagsRequest,
  UpdateAdminPartnerCategoryGroupRequest,
} from "@/types/admin-partner-category-group";
import type {
  AdminPartnerAttributionQuery,
  PartnerAttributionReport,
} from "@/types/partner-attribution";
import {
  PartnerCuratedList,
  PartnerDestinationAiBackfillResponse,
  PartnerDestinationAiRegenerateRequest,
  PartnerDestinationAiRegenerateResponse,
  PartnerDestinationAiStatus,
  PartnerDestinationAttractions,
  PartnerDestinationAttractionsRefreshResponse,
  PartnerDestinationDetail,
  PartnerDestinationProductsQuery,
  PartnerDestinationProductsResponse,
  PartnerDestinationSummary,
  PartnerDestinationSyncStatus,
  PartnerDestinationSyncTriggerResponse,
  UpdatePartnerCuratedListRequest,
} from "@/types/partner-destination";
import { BaseService } from "./BaseService";

class AdminPartnersService extends BaseService {
  private basePath = "/admin/partners/providers";

  async listProviders(): Promise<AdminPartnerProviderConfiguration[]> {
    return this.get<AdminPartnerProviderConfiguration[]>(this.basePath);
  }

  async upsertProvider(
    provider: string,
    data: UpsertAdminPartnerProviderRequest,
  ): Promise<AdminPartnerProviderConfiguration> {
    return this.put<AdminPartnerProviderConfiguration>(
      `${this.basePath}/${provider}`,
      data,
    );
  }

  async enableProvider(provider: string): Promise<void> {
    return this.patch<void>(`${this.basePath}/${provider}/enable`);
  }

  async disableProvider(provider: string): Promise<void> {
    return this.patch<void>(`${this.basePath}/${provider}/disable`);
  }

  // Per-hotel activity-limit override. Contract 2026-09-27 (backend
  // dev@52c7061): not under `basePath` — a hotel-scoped admin route that
  // requires the hotel's tenantId as a query param; a mismatched tenant
  // answers 404.
  async getHotelActivityLimit(
    hotelId: string,
    tenantId: string,
  ): Promise<PartnerHotelActivityLimit> {
    return this.get<PartnerHotelActivityLimit>(
      `/admin/hotels/${hotelId}/partners/limit`,
      { tenantId },
    );
  }

  // "Activities by room" report, Guesteria-admin scope (task 0041, contract
  // 2026-09-30) — same hotel-scoped-admin-route shape as `getHotelActivityLimit`
  // above: needs the hotel's own tenantId, a mismatched one 404s.
  async getHotelAttribution(
    hotelId: string,
    query: AdminPartnerAttributionQuery,
  ): Promise<PartnerAttributionReport> {
    return this.get<PartnerAttributionReport>(
      `/admin/partners/hotels/${hotelId}/attribution`,
      query,
      { skipSelectedHotelHeader: true },
    );
  }

  async updateHotelActivityLimit(
    hotelId: string,
    tenantId: string,
    data: UpdatePartnerHotelActivityLimitRequest,
  ): Promise<PartnerHotelActivityLimit> {
    return this.put<PartnerHotelActivityLimit>(
      `/admin/hotels/${hotelId}/partners/limit?tenantId=${encodeURIComponent(tenantId)}`,
      data,
    );
  }

  // Cross-tenant order rows and summary (contract confirmed 2026-09-27,
  // backend dev@8379566). Not wired into any page yet — no platform-wide
  // orders screen exists today.
  // `skipSelectedHotelHeader`, 2026-09-28: this is a cross-tenant summary —
  // sim hit a 400 "Region could not be determined" once this was wired into
  // `PartnerRevenueSummaryPage.tsx`. Every other file in this codebase for a
  // platform-admin/cross-tenant endpoint sets this flag
  // (`guesteria-admins-service.ts`, `admin-access-groups-service.ts`,
  // `admin-users-service.ts`, `platform-permissions-service.ts`, ...) —
  // without it, `BaseService` auto-attaches whatever hotel happens to be
  // selected in this admin's own browser storage as `x-hotel-id`, and the
  // backend's region resolution apparently keys off that header even though
  // it's meaningless for a request that spans every tenant.
  async getOrders(query: PartnerOrdersQuery): Promise<PartnerOrdersResponse> {
    return this.get<PartnerOrdersResponse>("/admin/partners/orders", query, {
      disableToast: true,
      skipSelectedHotelHeader: true,
    });
  }

  async getOrdersSummary(
    query: PartnerDateRangeQuery,
  ): Promise<AdminPartnerOrdersSummary> {
    return this.get<AdminPartnerOrdersSummary>(
      "/admin/partners/orders/summary",
      query,
      { disableToast: true, skipSelectedHotelHeader: true },
    );
  }

  // Task 0026 — curated city activity lists. Contract confirmed 2026-09-28
  // against backend dev@c9d0139c (.claude/tasks/0026-curated-city-activities.md).
  //
  // `disableToast: true` on every call here: sim found a raw error body
  // rendered on a failed fetch (2026-09-28), the same class of bug already
  // fixed once for partner orders (`getOrders`/`getOrdersSummary` above,
  // 2026-09-27). The Cities pages own their own fixed, translated error
  // text + HTTP status code (`describeError`, `partner-destinations.hooks.ts`)
  // instead of anything derived from the response body.
  private destinationsBasePath = "/admin/partners/destinations";

  async listDestinations(): Promise<PartnerDestinationSummary[]> {
    return this.get<PartnerDestinationSummary[]>(
      this.destinationsBasePath,
      undefined,
      {
        disableToast: true,
      },
    );
  }

  async getDestination(
    destinationId: string,
  ): Promise<PartnerDestinationDetail> {
    return this.get<PartnerDestinationDetail>(
      `${this.destinationsBasePath}/${destinationId}`,
      undefined,
      { disableToast: true },
    );
  }

  async syncDestination(
    destinationId: string,
  ): Promise<PartnerDestinationSyncTriggerResponse> {
    return this.post<PartnerDestinationSyncTriggerResponse>(
      `${this.destinationsBasePath}/${destinationId}/sync`,
      undefined,
      { disableToast: true },
    );
  }

  async getDestinationSyncStatus(
    destinationId: string,
  ): Promise<PartnerDestinationSyncStatus> {
    return this.get<PartnerDestinationSyncStatus>(
      `${this.destinationsBasePath}/${destinationId}/sync/status`,
      undefined,
      { disableToast: true },
    );
  }

  async getDestinationProducts(
    destinationId: string,
    query: PartnerDestinationProductsQuery,
  ): Promise<PartnerDestinationProductsResponse> {
    return this.get<PartnerDestinationProductsResponse>(
      `${this.destinationsBasePath}/${destinationId}/products`,
      query,
      { disableToast: true },
    );
  }

  async getCuratedList(destinationId: string): Promise<PartnerCuratedList> {
    return this.get<PartnerCuratedList>(
      `${this.destinationsBasePath}/${destinationId}/curated`,
      undefined,
      { disableToast: true },
    );
  }

  async updateCuratedList(
    destinationId: string,
    data: UpdatePartnerCuratedListRequest,
  ): Promise<PartnerDestinationSyncTriggerResponse> {
    return this.put<PartnerDestinationSyncTriggerResponse>(
      `${this.destinationsBasePath}/${destinationId}/curated`,
      data,
      { disableToast: true },
    );
  }

  // Task 0027 — AI auto-curation. Contract confirmed 2026-09-28 against
  // backend dev@b2d5a1e0 (see the type-level note in partner-destination.ts).
  async regenerateAi(
    destinationId: string,
    data: PartnerDestinationAiRegenerateRequest,
  ): Promise<PartnerDestinationAiRegenerateResponse> {
    return this.post<PartnerDestinationAiRegenerateResponse>(
      `${this.destinationsBasePath}/${destinationId}/ai/regenerate`,
      data,
      { disableToast: true },
    );
  }

  async getAiStatus(
    destinationId: string,
  ): Promise<PartnerDestinationAiStatus> {
    return this.get<PartnerDestinationAiStatus>(
      `${this.destinationsBasePath}/${destinationId}/ai/status`,
      undefined,
      { disableToast: true },
    );
  }

  // SUPER_ADMIN only — enforced by the backend; the UI also hides the
  // trigger for anyone else (`DestinationsPage.tsx`).
  async backfillAi(): Promise<PartnerDestinationAiBackfillResponse> {
    return this.post<PartnerDestinationAiBackfillResponse>(
      `${this.destinationsBasePath}/ai/backfill`,
      undefined,
      { disableToast: true },
    );
  }

  // Task 0032 — city attractions (Cities → city → "Attractions" tab).
  // Contract 2026-09-29.
  async getDestinationAttractions(
    destinationId: string,
  ): Promise<PartnerDestinationAttractions> {
    return this.get<PartnerDestinationAttractions>(
      `${this.destinationsBasePath}/${destinationId}/attractions`,
      undefined,
      { disableToast: true },
    );
  }

  async refreshDestinationAttractions(
    destinationId: string,
  ): Promise<PartnerDestinationAttractionsRefreshResponse> {
    return this.post<PartnerDestinationAttractionsRefreshResponse>(
      `${this.destinationsBasePath}/${destinationId}/attractions/refresh`,
      undefined,
      { disableToast: true },
    );
  }

  // Tasks 0028/0029 — Guesteria-staff-only tenant controls (SUPER_ADMIN /
  // GUESTERIA_ADMIN), shown in the tenant card on `TenantEditPage.tsx`.
  // Contract 2026-09-28.
  async getTenantPartnersEnabled(
    tenantId: string,
  ): Promise<AdminTenantPartnersSettings> {
    return this.get<AdminTenantPartnersSettings>(
      `/admin/tenants/${tenantId}/partners`,
      undefined,
      { disableToast: true },
    );
  }

  async updateTenantPartnersEnabled(
    tenantId: string,
    data: AdminTenantPartnersSettings,
  ): Promise<AdminTenantPartnersSettings> {
    return this.put<AdminTenantPartnersSettings>(
      `/admin/tenants/${tenantId}/partners`,
      data,
      { disableToast: true },
    );
  }

  async getTenantPartnerCommission(
    tenantId: string,
  ): Promise<TenantPartnerCommission> {
    return this.get<TenantPartnerCommission>(
      `/admin/tenants/${tenantId}/partner-commission`,
      undefined,
      { disableToast: true },
    );
  }

  async updateTenantPartnerCommission(
    tenantId: string,
    data: UpdateTenantPartnerCommissionRequest,
  ): Promise<TenantPartnerCommission> {
    return this.put<TenantPartnerCommission>(
      `/admin/tenants/${tenantId}/partner-commission`,
      data,
      { disableToast: true },
    );
  }

  // Task 0036 — Guesteria-admin-only hotel sync management, replacing the
  // hotel-scoped sync/logs endpoints (now 404). Contract 2026-09-29. Every
  // call takes the hotel's own tenantId as a query param.
  async triggerHotelSync(
    hotelId: string,
    tenantId: string,
  ): Promise<AdminHotelPartnerSyncTriggerResponse> {
    return this.post<AdminHotelPartnerSyncTriggerResponse>(
      `/admin/partners/hotels/${hotelId}/sync?tenantId=${encodeURIComponent(tenantId)}`,
      undefined,
      { disableToast: true },
    );
  }

  async getHotelSyncStatus(
    hotelId: string,
    tenantId: string,
  ): Promise<AdminHotelPartnerSyncStatus> {
    return this.get<AdminHotelPartnerSyncStatus>(
      `/admin/partners/hotels/${hotelId}/sync/status`,
      { tenantId },
      { disableToast: true },
    );
  }

  async updateHotelAutoSync(
    hotelId: string,
    tenantId: string,
    data: UpdateAdminHotelAutoSyncRequest,
  ): Promise<AdminHotelAutoSyncResponse> {
    return this.put<AdminHotelAutoSyncResponse>(
      `/admin/partners/hotels/${hotelId}/auto-sync?tenantId=${encodeURIComponent(tenantId)}`,
      data,
      { disableToast: true },
    );
  }

  // Provider event log, Guesteria staff only — `x-tenant-id` header, not a
  // hotelId path param (the hotel-scoped `/hotels/:hotelId/partners/logs`
  // 404s now). Contract 2026-09-29.
  async getPartnerLogs(
    tenantId: string,
    filters?: PartnerProviderLogsFilters,
  ): Promise<PartnerProviderLogsResponse> {
    return this.get<PartnerProviderLogsResponse>(
      "/admin/partners/logs",
      filters,
      {
        disableToast: true,
        headers: { "x-tenant-id": tenantId },
      },
    );
  }

  // "Sync bookings now" — synchronous, contract confirmed 2026-09-29 (task
  // 0033, backend dev@ec22d23c).
  async pollOrdersFeed(): Promise<AdminPartnerOrdersPollFeedResponse> {
    return this.post<AdminPartnerOrdersPollFeedResponse>(
      "/admin/partners/orders/poll-feed",
      undefined,
      { disableToast: true },
    );
  }

  // Booking report import (task 0033, contract 2026-09-29) — Viator
  // provider page, Guesteria staff only. `uploadFile` (shared with the
  // tenant-logo upload) has no `disableToast` option, so a 400 here may
  // also surface BaseService's own generic toast in addition to the fixed
  // translated text this feature's own UI renders — acceptable overlap,
  // not a raw-body leak either way.
  async importOrdersReport(file: File): Promise<PartnerOrdersImportResponse> {
    return this.uploadFile<PartnerOrdersImportResponse>(
      "/admin/partners/orders/import",
      file,
    );
  }

  async getOrdersImportHistory(
    limit = 20,
  ): Promise<PartnerOrdersImportHistoryEntry[]> {
    return this.get<PartnerOrdersImportHistoryEntry[]>(
      "/admin/partners/orders/imports",
      { limit },
      { disableToast: true },
    );
  }

  // DV-159 — guest-facing category groups (Partners → Categories). Global,
  // not hotel-scoped: no `x-hotel-id`. The panel shows its own fixed error
  // text, so no raw-body toast.
  private categoryGroupsBasePath = "/admin/partners/category-groups";

  private categoryGroupsOptions = {
    disableToast: true,
    skipSelectedHotelHeader: true,
  };

  async listCategoryGroups(): Promise<AdminPartnerCategoryGroup[]> {
    return this.get<AdminPartnerCategoryGroup[]>(
      this.categoryGroupsBasePath,
      undefined,
      this.categoryGroupsOptions,
    );
  }

  async listUnassignedCategoryTags(): Promise<AdminPartnerCategoryTag[]> {
    return this.get<AdminPartnerCategoryTag[]>(
      `${this.categoryGroupsBasePath}/unassigned-tags`,
      undefined,
      this.categoryGroupsOptions,
    );
  }

  async createCategoryGroup(
    data: CreateAdminPartnerCategoryGroupRequest,
  ): Promise<AdminPartnerCategoryGroup> {
    return this.post<AdminPartnerCategoryGroup>(
      this.categoryGroupsBasePath,
      data,
      this.categoryGroupsOptions,
    );
  }

  async updateCategoryGroup(
    id: string,
    data: UpdateAdminPartnerCategoryGroupRequest,
  ): Promise<AdminPartnerCategoryGroup> {
    return this.put<AdminPartnerCategoryGroup>(
      `${this.categoryGroupsBasePath}/${id}`,
      data,
      this.categoryGroupsOptions,
    );
  }

  async setCategoryGroupTags(
    id: string,
    data: SetAdminPartnerCategoryGroupTagsRequest,
  ): Promise<AdminPartnerCategoryGroup> {
    return this.put<AdminPartnerCategoryGroup>(
      `${this.categoryGroupsBasePath}/${id}/tags`,
      data,
      this.categoryGroupsOptions,
    );
  }

  async deleteCategoryGroup(id: string): Promise<void> {
    return this.delete<void>(
      `${this.categoryGroupsBasePath}/${id}`,
      undefined,
      this.categoryGroupsOptions,
    );
  }

  async autoAssignCategoryTags(): Promise<AdminPartnerCategoryAutoAssignResponse> {
    return this.post<AdminPartnerCategoryAutoAssignResponse>(
      `${this.categoryGroupsBasePath}/auto-assign`,
      undefined,
      this.categoryGroupsOptions,
    );
  }
}

export const adminPartnersService = new AdminPartnersService();
